// apps/web/src/lib/analytics-events.ts
import { capture } from '@intelhub/analytics/client'
import { BUCKETS } from '@intelhub/analytics/registry'
import { bucketValue } from '@intelhub/analytics/sanitize'
import type { StoryFilters, PublisherBadge } from '@/types/story'

const PAGING_KEYS = new Set(['cursor', 'limit'])

export function trackStoriesListViewed(filters: StoryFilters): void {
  const filter_count = Object.entries(filters)
    .filter(([k, v]) => !PAGING_KEYS.has(k) && v !== undefined && v !== null)
    .length
  const tier = filters.tier
  capture('stories:list_viewed', tier ? { filter_count, source_tier_filter: tier } : { filter_count })
}

// Typed against the real PublisherBadge ({ name, tier }); only `tier` is read.
export function trackStoryOpened(story: {
  publisherBadge: PublisherBadge
  earliestPublishedAt: string
}): void {
  const hours = Math.max(0, (Date.now() - new Date(story.earliestPublishedAt).getTime()) / 3_600_000)
  capture('story:opened', {
    item_source_tier: story.publisherBadge.tier,
    item_age_hours: bucketValue([...BUCKETS.item_age_hours], hours),
  })
}

/** Never send the query text — only its bucketed length (R6, R22). */
export function trackSearchPerformed(query: string, resultsCount: number): void {
  capture('search:performed', {
    search_term_length: bucketValue([...BUCKETS.search_term_length], query.trim().length),
    results_count: bucketValue([...BUCKETS.results_count], resultsCount),
  })
}

export function trackRegulatoryWatchAdded(frameworkSlug: string): void {
  capture('regulatory:watch_added', { framework_slug: frameworkSlug })
}

// Generic so real `Vendor` objects (which carry name/aliases/etc.) satisfy it
// without tripping TypeScript's excess-property check on object literals.
export function trackVendorTracked<T extends { id: string; slug?: string }>(
  vendors: readonly T[],
  vendorId: string,
): void {
  const slug = vendors.find((v) => v.id === vendorId)?.slug
  // No slug => no event. Emitting undefined would break the registry's type
  // contract at runtime; skipping the measurement is the correct degradation.
  if (!slug) return
  capture('vendor:tracked', { vendor_slug: slug })
}

export function trackFrameworkViewed(slug: string | undefined): void {
  if (!slug) return
  capture('regulatory:framework_viewed', { framework_slug: slug })
}

export function trackCalendarViewed(window: string): void {
  /* KAN-256. `window` is the closed token and is always sent; `window_days` is
   * the numeric dimension and is omitted for the unbounded `all` view.
   *
   * `Number('all')` is NaN, which is not JSON-representable and serialises to
   * null in transit — indistinguishable from a missing value. So the numeric
   * dimension is sent only when one exists, and the token carries the meaning.
   * The canonical contract (registry v1.2.0) types `window` as the union
   * '30' | '90' | '365' | 'all', so an unrecognised token fails to compile
   * rather than shipping silently. */
  const days = Number(window)
  capture('regulatory:calendar_viewed', {
    window: window as '30' | '90' | '365' | 'all',
    ...(Number.isFinite(days) ? { window_days: days } : {}),
  })
}

export function trackCompareUsed(): void {
  capture('regulatory:compare_used', {})
}

export function trackBenchmarkViewed(id: string | undefined): void {
  if (!id) return
  capture('benchmark:viewed', { benchmark_id: id })
}

export function trackProviderViewed(slug: string | undefined): void {
  if (!slug) return
  capture('provider:viewed', { provider_slug: slug })
}

export function trackMapViewed(rangeDays: number): void {
  capture('map:viewed', { time_window_days: rangeDays })
}

export function trackAnalyticsTabViewed(tab: 'decide' | 'trends' | 'compare'): void {
  capture('analytics:tab_viewed', { analytics_tab: tab })
}
